/**
 * Paleta de columnas, métricas y campos calculados.
 */
import React, { useMemo, useState } from 'react';
import { selectedColumnOrder, selectedColumnRole, sourceLabel, sourceRequestFields } from './engineConfig';
import FormulaEditorModal from './FormulaEditorModal';
import { columnToField, calculatedToField } from './fieldActions';
import { columnOrigins, originInfo, sourceSummary } from './fieldOrigins';
import { formulaFieldRefs } from './formulaLanguage';
import { studio, SearchField } from '../studioUi';

const FieldPalette = ({
    schemaColumns = [],
    dataSourceId,
    dataSources = [],
    joins = [],
    schemasBySource = {},
    onSelectSource,
    calculatedFields = [],
    draggingField,
    onDragField,
    onClickField,
    onAddCalculated,
    onUpdateCalculated,
    onRemoveCalculated,
    onHide
}) => {
    const [search, setSearch] = useState('');
    // null: cerrado; {} crea un campo; {id, …} edita ese campo.
    const [editorField, setEditorField] = useState(null);

    const q = search.trim().toLowerCase();
    const source = dataSources.find(item => item.id === dataSourceId);
    const picked = source?.selectedColumns;
    const visibleNames = selectedColumnOrder(source, schemaColumns);
    const byName = Object.fromEntries(schemaColumns.map(col => [col.name, col]));
    const visibleColumns = visibleNames.map(name => byName[name]).filter(Boolean);
    const sourceFields = visibleColumns.map(col => columnToField(col, dataSourceId, {
        role: selectedColumnRole(source, col.name, col.inferred_type),
        label: picked?.aliases?.[col.name] || col.name,
    }));
    const calcFields = calculatedFields.map(field => calculatedToField(field, calculatedFields));
    const match = (field) => !q
        || field.name.toLowerCase().includes(q)
        || String(field.label || '').toLowerCase().includes(q);

    const dimensions = sourceFields.filter(f => f.role === 'dimension' && match(f));
    const metrics = sourceFields.filter(f => f.role === 'metric' && match(f));
    const calcRow = calcFields.filter(f => f.level === 'row' && match(f));
    const calcAgg = calcFields.filter(f => f.level === 'agg' && match(f));

    // Hoja de origen de cada columna (en una unión, cada campo dice de qué hoja viene).
    const originCatalog = useMemo(
        () => ({ dataSources, joins, schemasBySource }),
        [dataSources, joins, schemasBySource]
    );
    const origins = useMemo(() => columnOrigins(dataSourceId, originCatalog), [dataSourceId, originCatalog]);
    const isJoinSource = source?.kind === 'join';
    const originsOf = (name) => (origins.get(name) || []).map(id => originInfo(id, originCatalog));

    const saveCalculated = (payload) => {
        if (editorField?.id) {
            const prev = calculatedFields.find(item => item.id === editorField.id);
            onUpdateCalculated && onUpdateCalculated(prev, payload);
        } else {
            onAddCalculated(payload);
        }
        setEditorField(null);
    };

    const dragFlag = React.useRef(false);

    const chipTitle = (field) => {
        if (field.formula) return `${field.name} = ${field.formula}`;
        const from = originsOf(field.name).map(origin => origin.label).join(' y ');
        const name = field.label && field.label !== field.name ? `${field.label} (${field.name})` : field.name;
        return from ? `${name} · de ${from}` : `${name} · arrastra al gráfico o a Dimensión / Métrica`;
    };

    const renderChip = (field, tone) => (
        <div
            key={field.id}
            draggable
            onDragStart={(e) => {
                dragFlag.current = true;
                e.dataTransfer.setData('text/plain', field.name);
                e.dataTransfer.effectAllowed = 'copy';
                onDragField(field);
            }}
            onDragEnd={() => {
                onDragField(null);
                setTimeout(() => { dragFlag.current = false; }, 0);
            }}
            onClick={() => {
                if (!dragFlag.current && onClickField) onClickField(field);
            }}
            style={{
                ...styles.chip,
                ...tones[tone],
                outline: draggingField?.id === field.id ? '2px solid #5E35B1' : 'none'
            }}
            title={chipTitle(field)}
        >
            {isJoinSource && field.kind !== 'calculated' && originsOf(field.name).map(origin => (
                <span key={origin.id} style={{ ...styles.originDot, backgroundColor: origin.color }} />
            ))}
            <span style={styles.chipName}>{field.label || field.name}</span>
            {field.kind === 'calculated' && (
                <>
                    <button
                        type="button"
                        style={styles.chipEdit}
                        title="Editar campo"
                        onClick={e => { e.stopPropagation(); setEditorField(calculatedFields.find(item => item.id === field.id) || field); }}
                    >✎</button>
                    <button
                        type="button"
                        style={styles.chipDelete}
                        onClick={e => { e.stopPropagation(); onRemoveCalculated(field); }}
                    >×</button>
                </>
            )}
        </div>
    );

    // Campos que el editor ofrece: columnas visibles de la fuente (con su hoja de origen) y los
    // demás calculados (con las hojas que usan sus fórmulas).
    const editorFields = useMemo(() => {
        if (!editorField) return [];
        const columnFields = visibleColumns.map(col => ({
            name: col.name,
            label: picked?.aliases?.[col.name] || col.name,
            type: col.inferred_type || 'string',
            group: selectedColumnRole(source, col.name, col.inferred_type) === 'dimension' ? 'dimension' : 'metric',
            origins: (origins.get(col.name) || []).map(id => originInfo(id, originCatalog)),
        }));
        const others = calculatedFields.filter(item => item.id !== editorField.id);
        const originsByName = new Map(columnFields.map(field => [field.name, field.origins]));
        const calcOrigins = (item, seen = new Set()) => {
            if (seen.has(item.name)) return [];
            seen.add(item.name);
            const found = new Map();
            formulaFieldRefs(item.formula || '', [...originsByName.keys(), ...others.map(o => o.name)]).forEach(ref => {
                const direct = originsByName.get(ref);
                const nested = others.find(o => o.name === ref);
                (direct || (nested ? calcOrigins(nested, seen) : [])).forEach(o => found.set(o.id, o));
            });
            return [...found.values()];
        };
        return [
            ...columnFields,
            ...others.map(item => ({
                name: item.name,
                label: item.name,
                type: item.inferred_type || 'number',
                group: 'calc',
                origins: calcOrigins(item),
            })),
        ];
    }, [editorField, visibleColumns, picked, source, calculatedFields, origins, originCatalog]);
    const editorSource = useMemo(
        () => (editorField ? sourceSummary(dataSourceId, originCatalog) : null),
        [editorField, dataSourceId, originCatalog]
    );
    const previewSource = useMemo(
        () => (dataSourceId ? sourceRequestFields({ dataSourceId }, { dataSources, joins }) : null),
        [dataSourceId, dataSources, joins]
    );

    return (
        <aside style={styles.panel}>
            <div style={styles.titleRow}>
                <h3 style={styles.title}>Campos</h3>
                {onHide && (
                    <button type="button" style={styles.hideBtn} onClick={onHide} title="Ocultar campos">‹</button>
                )}
            </div>
            {dataSources.length > 1 && (
                <select
                    value={dataSourceId || ''}
                    onChange={e => onSelectSource && onSelectSource(e.target.value)}
                    style={studio.controlCompact}
                    title="Campos de esta fuente"
                >
                    {dataSources.map(source => (
                        <option key={source.id} value={source.id}>{sourceLabel(source)}</option>
                    ))}
                </select>
            )}
            <SearchField
                value={search}
                onChange={setSearch}
                placeholder="Buscar campo…"
                style={{ marginBottom: 0 }}
            />
            <div className="creator-panel-scroll">
            <p style={styles.hint}>Arrastra a un gráfico o haz clic con un widget seleccionado.</p>

            <Section label="Dimensiones">{dimensions.map(f => renderChip(f, 'dim'))}</Section>
            <Section label="Métricas">{metrics.map(f => renderChip(f, 'met'))}</Section>
            <Section label="Calculados (fila)">{calcRow.map(f => renderChip(f, 'calc'))}</Section>
            <Section label="Ratios y agregados">{calcAgg.map(f => renderChip(f, 'ratio'))}</Section>

            <button type="button" style={styles.addCalc} onClick={() => setEditorField({})}>
                + Campo calculado
            </button>
            </div>
            {editorField && (
                <FormulaEditorModal
                    initial={editorField}
                    fields={editorFields}
                    takenNames={[
                        ...schemaColumns.map(col => col.name),
                        ...calculatedFields.filter(item => item.id !== editorField.id).map(item => item.name),
                    ]}
                    calculatedFields={calculatedFields
                        .filter(item => item.id !== editorField.id)
                        .map(item => ({ name: item.name, formula: item.formula }))}
                    previewSource={previewSource}
                    source={editorSource}
                    onSave={saveCalculated}
                    onClose={() => setEditorField(null)}
                />
            )}
        </aside>
    );
};

const Section = ({ label, children }) => (
    <div style={styles.section}>
        <div style={styles.sectionLabel}>{label}</div>
        <div style={styles.chipList}>{children}</div>
        {React.Children.count(children) === 0 && <div style={styles.empty}>Ninguno</div>}
    </div>
);

const tones = {
    dim: { background: '#e8f0fe', color: '#174ea6' },
    met: { background: '#e6f4ea', color: '#137333' },
    calc: { background: '#f3e8fd', color: '#6a1b9a' },
    ratio: { background: '#fff3e0', color: '#e65100' }
};

const styles = {
    panel: {
        width: '250px',
        flexShrink: 0,
        height: '100%',
        minHeight: 0,
        background: '#fff',
        border: '1px solid #dadce0',
        borderRadius: '12px',
        padding: '12px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        boxSizing: 'border-box'
    },
    titleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' },
    title: { margin: 0, fontSize: '1rem', color: '#5E35B1' },
    hideBtn: {
        border: '1px solid #e0d4f7',
        background: '#faf7ff',
        color: '#5E35B1',
        borderRadius: '6px',
        width: '26px',
        height: '26px',
        cursor: 'pointer',
        fontSize: '1rem',
        lineHeight: 1
    },
    hint: { margin: 0, fontSize: '0.72rem', color: '#5f6368', lineHeight: 1.4 },
    section: { display: 'flex', flexDirection: 'column', gap: '6px' },
    sectionLabel: { fontSize: '0.72rem', fontWeight: 700, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.04em' },
    chipList: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
    chip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px',
        borderRadius: '999px',
        fontSize: '0.75rem',
        fontWeight: 600,
        cursor: 'grab',
        maxWidth: '100%'
    },
    chipName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    originDot: { width: '7px', height: '7px', borderRadius: '999px', flexShrink: 0 },
    chipDelete: { border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit', fontWeight: 'bold', padding: 0, lineHeight: 1 },
    chipEdit: { border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit', fontWeight: 'bold', padding: 0, lineHeight: 1, fontSize: '0.72rem' },
    empty: { fontSize: '0.75rem', color: '#9aa0a6' },
    addCalc: {
        border: '1px dashed #d1c4e9',
        background: '#faf5ff',
        color: '#5E35B1',
        borderRadius: '8px',
        padding: '8px',
        cursor: 'pointer',
        fontWeight: 600
    }
};

export default FieldPalette;
